import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Divider from "../Divider/Divider";
import { isNullOrEmpty } from "@utils/helperFunction";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { colors } from "@utils/theme";
import {
  candidateRoutes,
  hiringCompanyRoutes,
  recruiterRoutes,
  referralAmbassadorRoutes,
} from "./Sidebar.const";
import { useForm } from "react-hook-form";
import CustomInput from "../CustomInput/CustomInput";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import regexList from "@utils/regexList";
import Button from "../button/button";
import { fetchLinkedToken, login } from "@endpoints/auth.service";
import { loginUser } from "@store/actions/auth.action";
import { useSnackbar } from "notistack";
import { linkAccount } from "@endpoints/user.service";
import { RootState } from "@store/reducers";
import { fetchNotificationCounts } from "@store/actions/notification.action";
import { NavLink } from "react-router-dom";
import TooltipPortal from "@components/TooltipPortal/TooltipPortal";

const Sidebar = () => {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(
    null
  );
  const pathname = useLocation().pathname;
  const [isOpen, setIsOpen] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const meetingCounts = useSelector(
    (state: RootState) => state.notification.meeting?.count
  );
  const linkedAccount = useSelector(
    (state: RootState) => state.auth.linkedAccount
  );
  const navigate = useNavigate();
  const noCreateProfile = pathname !== "/create-profile";
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      openLoginModel: false,
      isLoading: false,
    },
  });
  const fields = watch();
  const routes =
    user?.userType === userTypes.RECRUITER
      ? recruiterRoutes
      : user?.userType === userTypes.CANDIDATE
        ? candidateRoutes
        : user?.userType === userTypes?.HIRING_COMPANY
          ? hiringCompanyRoutes
          : referralAmbassadorRoutes;

  const [accountLinking, setAccountLinking] = useState(false);

  const showProfileToolTips = useMemo(() => {
    if (isNullOrEmpty(user)) {
      return false;
    }
    return ["jobTitle", "location", "about", "phoneNo", "address"].every(
      (key) => {
        return isNullOrEmpty(user[key]);
      }
    );
  }, [user]);

  const handleLogin = async (fields) => {
    try {
      setValue("isLoading", true);
      const { data } = await login(fields);

      setAccountLinking(true);
      const { data: _data } = await linkAccount(data.token);

      dispatch(loginUser(_data));
      // if (data?.user?.userType === userTypes.CANDIDATE) {
      //   await fetchCandidateDetails(data);
      // } else if (data?.user?.userType === userTypes.REFERRAL_AMBASSADOR) {
      //
      // }
      setValue("openLoginModel", false);
      window.location.reload();
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setValue("isLoading", false);
    }
  };

  const onClickLinkedAccount = async () => {
    const { data } = await fetchLinkedToken();
    dispatch(loginUser(data));
    window.location.reload();
  };

  useEffect(() => {
    dispatch(fetchNotificationCounts());
  }, []);

  useEffect(() => {
    if (!showProfileToolTips) return;

    const index = routes.findIndex((r) => r.route === "/my-profile");

    const el = itemRefs.current[index];
    if (!el) return;

    const rect = el.getBoundingClientRect();

    setTooltipStyle({
      position: "fixed",
      top: rect.top + rect.height / 2,
      left: rect.right - 70, // spacing from sidebar
      transform: "translateY(-50%)",
      zIndex: 9999,
    });
  }, [showProfileToolTips, routes, noCreateProfile]);

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="xl:hidden fixed top-4 left-4 z-20"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? "Close" : "Menu"}
      </button>

      {/* Sidebar */}
      <div
        className={`xl:static top-0 left-0 h-full bg-white z-10 transition-all duration-300 ease-in-out
                    ${isOpen ? "w-48" : "w-16 xl:w-48"}`}
      >
        <div
          className={`flex-col max-h-[99%] ${noCreateProfile ? "flex" : "hidden"
            } ${isOpen ? "w-48" : "w-16 pl-4 xl:w-48"
            } gap-3 pt-10 pb-5 overflow-y-scroll no-scrollbar`}
        >
          {routes?.map((route, index) => {
            const isCandidateProfile = user?.userType === EnumUserTypes.CANDIDATE && route?.route === "/my-profile";
            const isCandidateAspiration = user?.userType === EnumUserTypes.CANDIDATE && route?.route === "/aspiration";

            return (
              <NavLink to={route?.route} key={route.label}>
                {({ isActive }) => (
                  <div
                    className="flex flex-col"
                    key={route.label}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                  >
                    {((user?.userType === 3 && route?.label === "Settings") ||
                      (user?.userType === 2 && route?.label === "Settings") ||
                      (user?.userType === 4 && route?.label === "Profile")) && (
                        <Divider className="bg-[#DDDDDD] mb-1" />
                      )}
                    <div
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      className={`flex items-center pr-4 xl:pr-0 xl:justify-start relative gap-2 rounded-lg ${isActive ? "bg-[#F2F2F2]" : ""
                        } h-9 pl-2`}
                    >
                      <div className="flex gap-2 flex-1">
                        <div className="relative w-3.5">
                          {route?.icon}
                          {route?.label === "Calendar" && meetingCounts > 0 ? (
                            <div className="-right-3 -top-2 absolute font-medium bg-green-600 text-xss text-white rounded-full h-3.5 w-3.5 flex items-center justify-center xl:hidden">
                              {meetingCounts}
                            </div>
                          ) : null}
                        </div>
                        <label className="relative font-medium text-xs cursor-pointer text-[#4D4D4D] hidden xl:block">
                          {route?.label}
                          {route?.label === "Calendar" && meetingCounts > 0 ? (
                            <div className="-right-6 -top-2 absolute font-medium bg-green-600 text-xss text-white rounded-full h-3.5 w-3.5 flex items-center justify-center">
                              {meetingCounts}
                            </div>
                          ) : null}
                        </label>
                      </div>

                      {
                        isCandidateProfile ?
                          <div className="flex items-center justify-center text-white gap-2 h-6 w-6 rounded-full bg-green-600">
                            <span className="text-xs font-semibold">
                              {user?.status?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          :
                          null
                      }

                      {
                        isCandidateAspiration ?
                          <div className="flex items-center justify-center text-white gap-2 h-6 w-6 rounded-full bg-green-600">
                            <span className="text-xs font-semibold">
                              A
                            </span>
                          </div>
                          :
                          null
                      }

                      <div
                        className={`w-2 h-full ${isActive ? "bg-gray-500" : "white"
                          } rounded-l`}
                      />
                      {/* {showProfileToolTips &&
                        route?.route === "/my-profile" && (
                          <div className="absolute top-[-2px] left-7 xl:left-[60%] transform mb-2 whitespace-pre bg-gray-700 font-montserrat font-medium text-xs text-grey-100 rounded px-2 py-1 shadow-lg z-50">
                            Complete your profile to{"\n"}see more relevant
                            jobs.
                          </div>
                        )} */}
                      {/* {showProfileToolTips &&
                        tooltipStyle &&
                        noCreateProfile &&
                        route?.route === "/my-profile" && (
                          <TooltipPortal>
                            <div
                              style={tooltipStyle}
                              className="whitespace-pre bg-gray-700 font-montserrat font-medium text-xs text-grey-100 rounded px-2 py-1 shadow-lg z-[9999]"
                            >
                              Complete your profile to{"\n"}see more relevant
                              jobs.
                            </div>
                          </TooltipPortal>
                        )} */}
                    </div>
                    {/* {
                      // @ts-ignore
                      route?.showLive ? (
                        <div className="bg-green-600 text-white rounded-sm py-1 px-3 ml-7 font-medium text-xss w-max">
                          Active
                        </div>
                      ) : null
                    } */}
                  </div>
                )}
              </NavLink>
            );
          })}
          {/* {user?.userType !== userTypes.RECRUITER &&
            user?.userType !== userTypes.HIRING_COMPANY && (
              <div>
                <Divider className="bg-[#DDDDDD]" />
                <div>
                  <div className="flex flex-col bg-[#F1F5F9] mt-4 rounded-xl p-2 mr-2 flex-wrap max-xl:hidden ">
                    <span className="font-semibold text-[10px] text-light-grey">
                      Logged in as
                    </span>
                    <div className="flex items-center gap-1.5 mt-3">
                      <div className="h-9 w-9 rounded-full justify-center items-center flex bg-[#E1E0E0] bg-nwhited shadow-xl">
                        {user?.profilePic ? (
                          <img
                            src={user?.profilePic}
                            className="h-full w-full object-cover rounded-full"
                            alt="profileImg"
                          />
                        ) : (
                          <span className="font-montserrat text-white font-semibold text-xl">
                            {user?.firstName?.charAt(0)}
                            {user?.lastName?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="flex flex-1 font-montserrat font-medium text-xs text-primary">
                        {user?.firstName} {user?.lastName}
                      </span>
                      <Check width={14} height={14} color={colors.Green} />
                    </div>
                  </div>
                  <div
                    role="button"
                    className="flex py-2 my-2 mx-2 rounded hover:bg-gray-100"
                  >
                    <LogOut height={16} color="#4d4d4d" />
                    <span className="font-medium text-[10px] text-[#4D4D4D]">
                      Logout
                    </span>
                  </div>
                </div>
                
                <Divider className="bg-[#DDDDDD] " />

                {user?.linkedAccountId === undefined ? (
                  <div className="flex flex-col mt-2 rounded-xl py-2 pl-2 mr-2 flex-wrap max-xl:hidden ">
                    <>
                      <span className="font-semibold text-[10px] text-primary">
                        Already have a referral member account?
                      </span>
                    </>
                    <div
                      role="button"
                      className="flex flex-1 items-center bg-white text-xs pl-1 py-2 hover:bg-gray-100 rounded gap-2 font-medium text-primary mt-1"
                      onClick={() => setValue("openLoginModel", true)}
                    >
                      <PlusCircleIcon fill={colors.PrimaryBlack} />
                      Add account
                    </div>
                  </div>
                ) : (
                  <button
                    className="flex flex-col bg-[#F1F5F9] mt-4 rounded-xl p-2 mr-2 flex-wrap max-xl:hidden"
                    onClick={onClickLinkedAccount}
                  >
                    <span className="font-semibold text-[10px] text-light-grey">
                      Switch to
                    </span>
                    <div className="flex items-center gap-1.5 mt-3">
                      <div className="h-9 w-9 rounded-full justify-center items-center flex bg-[#E1E0E0] bg-nwhited shadow-xl">
                        {linkedAccount?.profilePic ? (
                          <img
                            src={linkedAccount?.profilePic}
                            className="h-full w-full object-cover rounded-full"
                            alt="profileImg"
                          />
                        ) : (
                          <span className="font-montserrat text-white font-semibold text-xl">
                            {linkedAccount?.firstName?.charAt(0)}
                            {linkedAccount?.lastName?.charAt(0)}
                          </span>
                        )}
                      </div>
                      <span className="flex flex-1 font-montserrat font-medium text-xs text-primary">
                        {linkedAccount?.firstName} {linkedAccount?.lastName}
                      </span>
                      <Check width={14} height={14} color={colors.Green} />
                    </div>
                  </button>
                )}

                <Divider className="bg-[#DDDDDD] " />
                <div className="flex mt-4 flex-col bg-[#F1F5F9] rounded-xl p-2 mr-2 flex-wrap max-xl:hidden">
                  <span className="font-semibold text-[10px] text-primary">
                    Interested in joining as a Referral Member?
                  </span>
                  <div
                    role="button"
                    className="flex items-center bg-white text-xs py-2 rounded-lg justify-center font-medium text-primary mt-3 hover:bg-gray-300"
                    onClick={() => navigate("/add-referral-account")}
                  >
                    Click Here!
                  </div>
                </div>
              </div>
            )} */}
        </div>
      </div>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-5 xl:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      {fields?.openLoginModel && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50  flex justify-center items-center z-50">
          <div className="flex flex-col gap-6 bg-white rounded-lg px-10 min-w-[25%] py-8">
            <label className="font-semibold text-3xl text-center mb-2">
              Login
            </label>
            <label className="text-light-black text-center font-medium">
              Please login to access your account
            </label>
            <form
              className="flex flex-col w-full pt-5"
              onSubmit={handleSubmit(handleLogin)}
            >
              {/* @ts-ignore */}
              <CustomInput
                label={"Email"}
                name="email"
                placeholder={"Enter your Email"}
                rules={{
                  required: "This field is required.",
                  pattern: {
                    value: regexList.email,
                    message: "Please enter a valid email.",
                  },
                }}
                control={control}
                errors={errors}
              />
              <div className="h-6" />
              {/* @ts-ignore */}
              <CustomInput
                label={"Password"}
                placeholder={"Enter your Password"}
                name="password"
                rules={{
                  required: "This field is required.",
                  pattern: {
                    value: regexList.password,
                    message: "Please enter a valid password.",
                  },
                }}
                control={control}
                errors={errors}
              />
              <div className="flex gap-3">
                <Button
                  type="submit"
                  variant="outline"
                  color={colors.TextPrimary}
                  onClick={() => setValue("openLoginModel", false)}
                  className="flex flex-1 items-center justify-center rounded-full mt-10 py-3"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-secondary flex flex-1 items-center justify-center flex-row rounded-full mt-10 py-3"
                >
                  <div className="flex">
                    {fields?.isLoading && <LoadingAnimation />}
                    Login
                  </div>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
